import { IntegrationCard } from './IntegrationCard';
import { IntegrationTile } from './integrationLogos';
import { useState } from 'react';
import { useIntegration, useConnectAsana, useDisconnectAsana } from '../../hooks/useIntegrations';
import { useIntegrationAvailability } from '../../hooks/useIntegrationAvailability';
import { log } from '@/lib/logger';

log.info('[AsanaIntegrationCard] Module loading...');

const CARD_LABELS = { asana: 'Asana', clickup: 'ClickUp', github: 'GitHub', basecamp: 'Basecamp', monday: 'monday.com' } as const;
const CARD_ICON_BG = { asana: 'var(--color-asana)', clickup: 'var(--color-clickup)', github: 'var(--color-github)', basecamp: 'var(--color-basecamp)', monday: 'var(--oq-palette-neutral-700)' } as const;
type CardProvider = keyof typeof CARD_LABELS;

export function AsanaIntegrationCard({ provider = 'asana' }: { provider?: CardProvider }) {
  const label = CARD_LABELS[provider];
  const { integration, isConnected, isLoading, refetch } = useIntegration(provider);
  const { available, reason: unavailableReason } = useIntegrationAvailability(provider, provider === 'asana');
  const connectMutation = useConnectAsana(provider);
  const disconnectMutation = useDisconnectAsana(provider);

  const [actionError, setActionError] = useState<string | null>(null);

  const isConnecting = connectMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;

  const handleConnect = () => {
    // Never fire a connect the backend can't complete — it's a guaranteed 500.
    if (!available) return;
    setActionError(null);
    connectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || `Failed to connect ${label}. Try again.`);
      },
    });
  };

  const handleDisconnect = () => {
    setActionError(null);
    disconnectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || `Failed to disconnect ${label}. Try again.`);
      },
    });
  };

  const metadata = integration?.providerMetadata;
  return (
    <IntegrationCard
      provider={provider}
      loading={isLoading}
      tile={<IntegrationTile color={CARD_ICON_BG[provider]}>{provider === 'asana' ? <AsanaIcon /> : provider === 'github' ? <GitHubIcon /> : provider === 'basecamp' ? <BasecampIcon /> : provider === 'monday' ? <BoardIcon /> : <ClickUpIcon />}</IntegrationTile>}
      name={label}
      description={`Push captured issues to ${label} with screenshots and full technical context.`}
      connection={{
        connected: isConnected,
        connecting: isConnecting,
        disconnecting: isDisconnecting,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onRetry: () => void refetch(),
        available,
        unavailableReason,
        connectedAt: integration?.createdAt,
        // Asana keeps its workspace; ClickUp and GitHub the signed-in username; Basecamp and monday.com the account.
        rows: provider === 'asana' ? [['Workspace', metadata?.asanaWorkspaceName]]
          : [['Account', provider === 'clickup' || provider === 'github' ? metadata?.username : metadata?.accountName]],
      }}
      errors={[actionError]}
    />
  );
}

function ClickUpIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M2 18.439l3.69-2.828c1.961 2.56 4.044 3.739 6.363 3.739 2.307 0 4.33-1.166 6.203-3.704L22 18.405C19.298 22.065 15.941 24 12.053 24 8.178 24 4.788 22.078 2 18.439zM12.04 6.15l-6.568 5.66-3.036-3.52L12.055 0l9.543 8.296-3.05 3.509z" />
    </svg>
  );
}

/**
 * A generic board glyph, NOT monday.com's mark: monday's brand rules forbid the
 * capsules on their own or in one colour (brand-monday.com/logo, read 2026-10-25),
 * and these tiles draw a single white glyph. Swap in their official full-colour
 * avatar once that is decided.
 */
function BoardIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M3 4h4v16H3zM10 4h4v11h-4zM17 4h4v7h-4z" />
    </svg>
  );
}

function BasecampIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 3a9 9 0 0 0-8.1 12.9A8.2 8.2 0 0 1 12 12a8.2 8.2 0 0 1 8.1 3.9A9 9 0 0 0 12 3zm0 11a6.2 6.2 0 0 0-6.4 3.3A9 9 0 0 0 12 21a9 9 0 0 0 6.4-3.7A6.2 6.2 0 0 0 12 14z" />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function AsanaIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="50" cy="28" r="16" fill="white"/>
      <circle cx="28" cy="68" r="16" fill="white"/>
      <circle cx="72" cy="68" r="16" fill="white"/>
    </svg>
  );
}

log.info('[AsanaIntegrationCard] Module loaded');
